import {
  Box,
	Button,
	Drawer,
	DrawerOverlay,
	IconButton,
	Input,
	InputGroup,
	InputRightElement,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	SimpleGrid,
	Stack,
	useColorModeValue as mode,
	useDisclosure,
  useToast
} from "@chakra-ui/react";
import _ from "lodash";
import { BsFillCaretDownFill, BsSearch } from "react-icons/bs";
import { GrAscend, GrDescend } from "react-icons/gr";
import React, {useEffect, useState} from "react";
import {Raider} from "../../../types/raider";
import { CardWithAvatar } from "../card/CardWithAvatar";
import { RaiderInfo } from "../card/RaiderInfo";
import QuestDrawer from "../drawers/QuestDrawer";
import AlchemyDrawer from "../drawers/AlchemyDrawer";
import StatusDrawer from "../drawers/StatusDrawer";
import StatsDrawer from "../drawers/StatsDrawer";
import { EconomyTool } from "../../../pages/alchemy";
import { EventLog } from "../../../pages/mob_mountain";

export interface MountainParamsType {
  readonly allRunners: number[];
  readonly mountainAction: (raider_id: number) => void;
  readonly approved: boolean;
  readonly approveAction: () => void;
  readonly eventLogs: EventLog[];
  readonly isApproving: boolean;
  readonly currentRunners: number[];
  
}

interface RaiderCardLayoutProps {
  isLoading: boolean;
  raiders: Raider[]
  button: string;
  tools?: EconomyTool[];
  updateTools?: () => Promise<void>;
  balances?: Record<string,number>;
  mountainParams?: MountainParamsType
}

export default function RaiderCardLayout(props: RaiderCardLayoutProps) {
  const {isLoading, raiders, button} = props;
  const [sortDirection, setSortDirection] = useState<boolean>(true);
  const [sortBy, setSortBy] = useState<string>("level");
  const [findRaider, setFindRaider] = useState<string>("");
  const [selectedRaider, setSelectedRaider] = useState<Raider>();
  const [sortedRaiders, setSortedRaiders] = useState<Raider[]>(raiders);


  const toast = useToast();

  const statusDrawer = { ...useDisclosure() };
	const questDrawer = { ...useDisclosure() };
	const statsDrawer = { ...useDisclosure() };
	const alchemyDrawer = { ...useDisclosure() };

  const openLeftPane = (raider: Raider) => {
		setSelectedRaider(raider);
		statsDrawer.onOpen();
	};

	const openQuestPane = (raider: Raider) => {
		setSelectedRaider(raider);
		questDrawer.onOpen();
	};
	
  const openAlchemyPane = (raider: Raider) => {
		setSelectedRaider(raider);
		alchemyDrawer.onOpen();
	};

	const openStatusPane = (raider: Raider) => {
		setSelectedRaider(raider);
		statusDrawer.onOpen();
	};


  const sorterNameCleanup = () => {
    if (sortBy == "attributes[1].value") {
      return "Level"
    } else if (sortBy == "attributes[3].value") {
      return "Generation"
    } else {
      return "Sort"
    }
  }

  useEffect(() => {
    let actual = JSON.parse(JSON.stringify(sortedRaiders)) as Raider[];
    actual = _.orderBy(
      _.filter(raiders, (raider) =>
        raider.name.toLowerCase().includes(findRaider.toLowerCase())
      ),
      [sortBy],
      [sortDirection ? "desc" : "asc"]
    )
    setSortedRaiders(actual);
  }, [sortDirection, sortBy,raiders])

  return (
    <>
      <Box maxW="7xl" mx="auto" mb={4} hidden={isLoading}>
        <Stack
          spacing="5"
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
        >
          <InputGroup maxW={{ md: "520px" }} w="full">
            <InputRightElement color="gray.400" pointerEvents="none">
              <BsSearch />
            </InputRightElement>
            <Input
              value={findRaider}
              onChange={(e) => setFindRaider(e.target.value)}
              bg={mode("gray.50", "gray.800")}
              placeholder="Find a raider"
            />
          </InputGroup>
          <Stack
            direction={{ base: "column", md: "row" }}
            spacing="4"
            justify="flex-end"
            flex="1"
            w={{ base: "full", md: "auto" }}
          >
            <Stack display="inline-flex" direction="row" spacing="4">
              <Menu placement="bottom">
                <MenuButton
                  as={Button}
                  variant="outline"
                  colorScheme="blue"
                  rightIcon={<BsFillCaretDownFill fontSize="0.8em" />}
                >
                  {sorterNameCleanup()}
                </MenuButton>
                <MenuList zIndex="2">
                  <MenuItem onClick={() => setSortBy("attributes[1].value")}>Level</MenuItem>
                  <MenuItem onClick={() => setSortBy("attributes[3].value")}>Generation</MenuItem>
                </MenuList>
              </Menu>
              <IconButton
                onClick={() => setSortDirection(!sortDirection)}
                aria-label="Direction"
                colorScheme="blue"
                icon={sortDirection ? <GrDescend /> : <GrAscend />}
              />
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 4 }}
        spacing="6"
        hidden={isLoading}
      >
        {sortedRaiders.map((raider) => (
          <CardWithAvatar
            key={"raider-" + raider.id}
            avatarProps={{ raider }}
          >
            <RaiderInfo
              mt={3}
              mb={3}
              raider={raider}
              width="100%"
              button={button}
              openLeftPane={openLeftPane}
              openAlchemyPane={openAlchemyPane}
              openQuestPane={openQuestPane}
              openStatusPane={openStatusPane}
              hide_button={props.mountainParams != undefined}
              mountainParams={props.mountainParams}
            />
          </CardWithAvatar>
        ))}
      </SimpleGrid>

      <Drawer
				isOpen={questDrawer.isOpen}
				placement="right"
				onClose={questDrawer.onClose}
				size="lg"
			>
				<DrawerOverlay />
				<QuestDrawer
					raider={selectedRaider!}
					closeDrawer={questDrawer.onClose}
				/>
			</Drawer>
      <Drawer
				isOpen={alchemyDrawer.isOpen}
				placement="right"
				onClose={alchemyDrawer.onClose}
				size="md"
			>
				<DrawerOverlay />
				<AlchemyDrawer
					raider={selectedRaider!}
					closeDrawer={alchemyDrawer.onClose}
          tools={props.tools!}
          balances={props.balances!}
          updateTools={props.updateTools!}
				/>
			</Drawer>
			<Drawer
				isOpen={statusDrawer.isOpen}
				placement="right"
				onClose={statusDrawer.onClose}
				size="lg"
			>
				<DrawerOverlay />
				<StatusDrawer
					raider={selectedRaider!}
					closeDrawer={statusDrawer.onClose}
				/>
			</Drawer>
			<Drawer
				isOpen={statsDrawer.isOpen}
				placement="left"
				onClose={statsDrawer.onClose}
				size="md"
			>
				<DrawerOverlay />
				<StatsDrawer
					all_my_raiders={raiders}
					raider={selectedRaider!}
					closeDrawer={statsDrawer.onClose}
				/>
			</Drawer>
    </>
  )
}
